import { useState, useEffect } from 'react';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Trash2, AlertTriangle, XCircle } from 'lucide-react';
import { policyDependencyService, type PolicyDependencyReport } from '@/services/leave/policyDependencyService';

interface PolicyDeletionDialogProps {
  policyId: string | null;
  policyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function PolicyDeletionDialog({
  policyId,
  policyName,
  open,
  onOpenChange,
  onConfirm
}: PolicyDeletionDialogProps) {
  const [dependencyReport, setDependencyReport] = useState<PolicyDependencyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');

  // Check dependencies when dialog opens
  useEffect(() => {
    if (open && policyId) {
      checkDependencies();
      setConfirmationText(''); // Reset confirmation
    }
  }, [open, policyId]);

  const checkDependencies = async () => {
    if (!policyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const report = await policyDependencyService.checkPolicyDependencies(policyId);
      setDependencyReport(report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check dependencies');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    onConfirm();
    onOpenChange(false);
  };

  const canDelete = dependencyReport?.canDelete || false;
  const hasBlockingDependencies = (dependencyReport?.blockingDependencies || 0) > 0;
  const isConfirmationValid = confirmationText.toLowerCase() === policyName.toLowerCase();

  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-8 space-y-3">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <div className="text-center">
        <p className="text-sm font-medium">Checking dependencies...</p>
        <p className="text-xs text-muted-foreground">
          Verifying it's safe to permanently delete this policy
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-8 space-y-3">
      <AlertTriangle className="h-8 w-8 text-destructive" />
      <div className="text-center">
        <p className="text-sm font-medium text-destructive">Failed to load dependency information</p>
        <p className="text-xs text-muted-foreground">{error}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={checkDependencies}
        >
          Try Again
        </Button>
      </div>
    </div>
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Permanently Delete Policy
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base">
            You are about to permanently delete "<span className="font-semibold">{policyName}</span>". 
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {loading && renderLoadingState()}
          
          {error && renderErrorState()}
          
          {dependencyReport && !loading && !error && (
            <>
              {/* Status Banner */}
              <div className={`p-4 rounded-lg border ${
                canDelete 
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-2">
                  {canDelete ? (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className={`font-medium ${canDelete ? 'text-yellow-800' : 'text-red-800'}`}>
                      {canDelete ? 'Policy can be deleted' : 'Cannot delete policy'}
                    </p>
                    <p className={`text-sm ${canDelete ? 'text-yellow-700' : 'text-red-700'}`}>
                      {canDelete 
                        ? 'This will permanently remove all policy data including balances and assignments.'
                        : 'Please resolve blocking dependencies before attempting deletion.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Blocking Dependencies */}
              {hasBlockingDependencies && (
                <>
                  <Separator />
                  <Card className="border-destructive/50 bg-destructive/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2 text-destructive">
                        <XCircle className="h-4 w-4" />
                        Blocking Dependencies
                        <Badge variant="destructive">
                          {dependencyReport.blockingDependencies}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-sm text-destructive">
                        {policyDependencyService.generateDependencyMessage(dependencyReport)}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* Warning about what will be deleted */}
              {canDelete && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-2 text-destructive">What will be permanently deleted:</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• The leave policy and all its configuration</li>
                      <li>• All employee assignments to this policy</li>
                      <li>• All leave balances associated with this policy</li>
                      <li>• Policy approver assignments</li>
                      <li>• Accrual rules and settings</li>
                    </ul>
                  </div>

                  {/* Confirmation Input */}
                  <div>
                    <p className="text-sm font-medium mb-2">Type the policy name to confirm deletion:</p>
                    <Input
                      placeholder={`Type "${policyName}" here`}
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      className={isConfirmationValid ? 'border-green-500' : ''}
                    />
                    {confirmationText && !isConfirmationValid && (
                      <p className="text-xs text-destructive mt-1">
                        Policy name doesn't match
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirmationText('')}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={loading || !!error || hasBlockingDependencies || !isConfirmationValid}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Checking...
              </>
            ) : hasBlockingDependencies ? (
              'Cannot Delete'
            ) : !isConfirmationValid ? (
              'Confirm Policy Name'
            ) : (
              'Permanently Delete'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}